import React, { createContext, useContext, useEffect, useState } from "react";
import * as webllm from "@mlc-ai/web-llm";
import modelManager, { hardwareDetector } from "../services/modelManager";

// Check if Web Workers and Service Workers are supported
const webWorkersSupported = typeof Worker !== 'undefined';
const serviceWorkersSupported = 'serviceWorker' in navigator;

// Check WebLLM compatibility
const checkWebLlmSupport = () => {
  const support = {
    webAssembly: typeof WebAssembly !== 'undefined',
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    cache: typeof caches !== 'undefined',
    indexedDB: typeof indexedDB !== 'undefined'
  };

  console.log('WebLLM compatibility check:', support);

  if (!support.webAssembly) {
    console.error('WebAssembly not supported - required for WebLLM');
    return false;
  }

  if (!support.cache && !support.indexedDB) {
    console.error('Neither Cache API nor IndexedDB available - required for WebLLM model storage');
    return false;
  }

  return true;
};

const webLlmSupport = checkWebLlmSupport();

type WebLlmStatus = "idle" | "loading" | "ready" | "error" | "streaming";

type WebLlmBackend = "main-thread" | "web-worker" | "service-worker";

interface WebLlmContextValue {
  engine: webllm.MLCEngineInterface | null;
  status: WebLlmStatus;
  progress: number;
  currentModelId: string | null;
  modelUrl: string | null;
  setModelUrl: (url: string | null) => void;
  switchModel: (modelId: string) => Promise<void>;
  isSupported: boolean;
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  backend: WebLlmBackend;
  cachedModels: string[];
  hardwareInfo: any;
  cpuCores: number;
}

const WebLlmContext = createContext<WebLlmContextValue | undefined>(undefined);

export const useWebLlm = () => {
  const ctx = useContext(WebLlmContext);
  if (!ctx) throw new Error("useWebLlm must be used within WebLlmProvider");
  return ctx;
};

interface Props {
  children: React.ReactNode;
}

export const WebLlmProvider: React.FC<Props> = ({ children }) => {
  const [engine, setEngine] = useState<webllm.MLCEngineInterface | null>(null);
  const [status, setStatus] = useState<WebLlmStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [backend, setBackend] = useState<WebLlmBackend>("main-thread");
  const [cachedModels, setCachedModels] = useState<string[]>([]);
  const [hardwareInfo, setHardwareInfo] = useState<any>(null);
  const cpuCores = navigator.hardwareConcurrency || 4;

  // Model switching function
  const switchModel = async (modelId: string): Promise<void> => {
    if (currentModelId === modelId) {
      console.log(`Model ${modelId} already loaded, skipping switch`);
      return;
    }

    console.log(`Switching model from ${currentModelId} to ${modelId}`);

    try {
      setStatus("loading");
      setProgress(10); // Show initial progress

      // Check if model is cached
      const isCached = modelManager.isModelCached(modelId);
      if (!isCached) {
        console.log(`Model ${modelId} not cached, loading to cache...`);
        setProgress(25); // Show cache loading progress
        await modelManager.loadModelToCache(modelId);
        setProgress(50); // Cache loaded
      } else {
        setProgress(50); // Already cached
      }

      // Get model URL
      const newModelUrl = modelManager.getModelUrl(modelId);
      if (!newModelUrl) {
        throw new Error(`No URL found for model ${modelId}`);
      }

      // If engine exists and supports reload, use it
      if (engine && 'reload' in engine) {
        console.log('Using engine reload for model switch');
        setProgress(75); // Show we're in reload phase
        try {
          await (engine as any).reload(newModelUrl, {
            initProgressCallback: (p: any) => {
              console.log('Model reload progress:', p.progress);
              // Map reload progress (0-1) to 75-100 range
              const reloadProgress = 75 + Math.round(p.progress * 25);
              setProgress(reloadProgress);
            }
          });
          console.log('Engine reload completed successfully');
          setProgress(100);
        } catch (reloadError) {
          console.error('Engine reload failed:', reloadError);
          throw reloadError;
        }
      } else {
        // Create new engine
        console.log('Creating new engine for model switch');
        const hardwareInfo = await hardwareDetector.detectHardware();
        const webLlmConfig = hardwareDetector.getWebLlmConfig();

        if (webLlmConfig.cacheBackend) {
          (webllm.prebuiltAppConfig as any).cacheBackend = webLlmConfig.cacheBackend;
        }

        let newEngine: webllm.MLCEngineInterface;

        setProgress(75); // Starting engine creation

        // Suppress Chrome Windows powerPreference warning
        const originalWarn = console.warn;
        console.warn = function(...args) {
          if (args[0] && typeof args[0] === 'string' && args[0].includes('powerPreference option is currently ignored when calling requestAdapter() on Windows')) {
            return;
          }
          originalWarn.apply(console, args);
        };

        // Use main thread for now - web workers have import issues
        setBackend("main-thread");
        console.log('Creating main thread WebLLM engine...');
        newEngine = await webllm.CreateMLCEngine(newModelUrl, {
          initProgressCallback: (p) => {
            console.log('Main thread model load progress:', p.progress);
            const engineProgress = 75 + Math.round(p.progress * 25);
            setProgress(engineProgress);
          }
        });
        console.log('Main thread WebLLM engine created successfully');

        console.log('Engine creation completed');
        setProgress(100);
        setEngine(newEngine);
      }

      setCurrentModelId(modelId);
      setModelUrl(newModelUrl);
      setStatus("ready");

      // Update cached models list
      const updatedCachedModels = modelManager.getCachedModels().map(entry => entry.modelId);
      setCachedModels(updatedCachedModels);

      console.log(`Successfully switched to model: ${modelId}`);

    } catch (error) {
      console.error(`Failed to switch to model ${modelId}:`, error);
      setStatus("error");
      setProgress(0); // Reset progress on error
    }
  };

  // Preloading removed - using on-demand loading only

  // Initialize cached models list - DELAYED LOAD
  // Wait 3 seconds after full app render before loading any model systems
  // Can be cancelled and initialized immediately if user interacts early
  useEffect(() => {
    let initStarted = false;
    
    const initializeCache = async () => {
      if (initStarted) return;
      initStarted = true;
      
      try {
        // Detect hardware on initialization
        const hwInfo = await hardwareDetector.detectHardware();
        setHardwareInfo(hwInfo);
        
        await modelManager.initialize();
        const cachedModelIds = modelManager.getCachedModels().map(entry => entry.modelId);
        setCachedModels(cachedModelIds);
        console.log('Initialized cached models:', cachedModelIds);
      } catch (error) {
        console.error('Failed to initialize model cache:', error);
      }
    };

    // Delayed initialization - 3000ms after component mount
    // This ensures application loads completely and becomes interactive first
    const timer = setTimeout(initializeCache, 3000);
    
    // Expose immediate initialization method for early user interaction
    (window as any).initializeModelCacheNow = () => {
      clearTimeout(timer);
      return initializeCache();
    };
    
    return () => {
      clearTimeout(timer);
      delete (window as any).initializeModelCacheNow;
    };
  }, []);

  // Service workers removed - using main thread only

  useEffect(() => {
    if (!modelUrl) return;

    let cancelled = false;

    const load = async () => {
      console.log('WebLlmProvider loading model:', modelUrl);

      // Check if required APIs are available
      if (!webLlmSupport) {
        console.error('WebLLM is not supported in this environment');
        setStatus('error');
        return;
      }

      setStatus("loading");
      setProgress(0);

      try {
        // Detect hardware and configure WebLLM accordingly
        const hardwareInfo = await hardwareDetector.detectHardware();
        const webLlmConfig = hardwareDetector.getWebLlmConfig();

        // Force IndexedDB storage - disable Cache API completely
        (webllm.prebuiltAppConfig as any).cacheBackend = "indexeddb";
        (webllm.prebuiltAppConfig as any).useCacheAPI = false;

        // Log hardware detection results
        console.log('WebLLM hardware configuration:', {
          recommendedBackend: hardwareInfo.recommendedBackend,
          hasNvidiaGPU: hardwareInfo.hasNvidiaGPU,
          hasOllama: hardwareInfo.hasOllama,
          cacheBackend: webLlmConfig.cacheBackend,
          webWorkersSupported
        });

        let eng: webllm.MLCEngineInterface;

        // Determine which backend to use based on availability and preferences
        // Use main thread only - web workers and service workers removed due to ES module compatibility issues
        console.log('Using main thread for WebLLM engine');
        setBackend("main-thread");

        eng = await webllm.CreateMLCEngine(modelUrl, {
          initProgressCallback: (p) => {
            if (cancelled) return;
            console.log('Model loading progress (Main Thread):', p.progress);
            setProgress(Math.round(p.progress * 100));
          }
        });

        if (cancelled) return;
        console.log('WebLLM model loaded successfully');
        setEngine(eng);
        setStatus("ready");
      } catch (err) {
        console.error("WebLLM loading error:", err);
        console.error("Model URL attempted:", modelUrl);

        // Provide more specific error messages
        let errorMessage = "Unknown error";
        if (err instanceof Error) {
          if (err.message.includes('caches is not defined')) {
            errorMessage = "Cache API not available. Try using a different browser or enable experimental features.";
          } else if (err.message.includes('ModelNotFoundError')) {
            errorMessage = "Model not found. Please check the model identifier.";
          } else {
            errorMessage = err.message;
          }
        }

        console.error("Error details:", errorMessage);
        if (!cancelled) setStatus("error");
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [modelUrl]);

  return (
    <WebLlmContext.Provider
      value={{
        engine,
        status,
        progress,
        currentModelId,
        modelUrl,
        setModelUrl,
        switchModel,
        isSupported: webLlmSupport,
        isStreaming,
        setIsStreaming,
        backend,
        cachedModels,
        hardwareInfo,
        cpuCores
      }}
    >
      {children}
    </WebLlmContext.Provider>
  );
};